<template>
  <div class="strategy-modal">
    <div ref="strategyEditModal" class="modal fade" data-bs-backdrop="static" id="strategyEditModal" tabindex="-1">
      <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable" v-draggable-modal>
        <div class="modal-content bg-primary">

          <div class="modal-header">
            <div class="h5 modal-title text-light me-2"> {{ modalTitle }} </div>
            <DocsInfoLink color="light" target="createStrategy"></DocsInfoLink>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>

          <div class="modal-body bg-light">
            <!-- Strategy -->
            <div v-if="elementType === 'strategy'" class="form-link">
              <div v-if="mode === 'add'" class="form-link">
                <div class="mb-3">
                  <div class="form-check form-switch">
                    <input v-model="form.strategy.folder.content" type="checkbox" role="switch" id="strategyFolderCheck" class="form-check-input" :checked="form.strategy.folder.content">
                    <label for="strategyFolderCheck" class="form-check-label">{{ $t("views.strategy.edit.folder") }}</label>
                  </div>
                </div>
              </div>
              <div class="mb-3">
                <label for="orgUnit" class="form-label">{{ $t("views.objective.edit.orgUnit") }}<span class="text-danger">*</span></label>
                <select v-model="form.strategy.orgUnit.content" id="orgUnit" class="form-select" >
                  <option v-for="orgUnit in orgUnitsList" :key="orgUnit.id" :value="orgUnit.id" :disabled="mode === 'update' && orgUnit.id === selectedOrgUnit.id ? true : false">
                    {{ orgUnit.name }}
                  </option>
                </select>
              </div>
              <div class="mb-3">
                <label for="strategyParentId" class="form-label">{{ $t("views.strategy.edit.parentStrategy") }}</label>
                <select v-model="form.strategy.parentId.content" id="strategyParentId" class="form-select">
                  <option value="" :disabled="!isAdmin">{{ $t("views.strategy.edit.noParentStrategy") }}</option>
                  <option v-for="strategy in strategiesList" :key="strategy.id" :value="strategy.id" :disabled="mode === 'update' && strategy.id === selectedStrategy.id ? true : false">
                    {{ strategy.name }}
                  </option>
                </select>
              </div>
              <div class="mb-3">
                <label for="strategyName" class="form-label">{{ $t("views.strategy.edit.name") }}<span class="text-danger">*</span></label>
                <input v-model="form.strategy.name.content" id="strategyName" :class="form.strategy.name.error === '' ? 'form-control' : 'form-control is-invalid'">
                <div class="invalid-feedback">{{ form.strategy.name.error }}</div>
              </div>
              <div v-if="!form.strategy.folder.content" class="row mb-3">
                <div class="col">
                  <label for="strategyResponsible" class="form-label">{{ $t("views.strategy.edit.responsible") }}</label>
                  <select v-model="form.strategy.responsible.content" id="strategyResponsible" :class="form.strategy.responsible.error === '' ? 'form-select' : 'form-select is-invalid'">
                    <option v-for="employee in employees" :key="employee.id" :value="employee.id">
                      {{ employee.lastName }}, {{ employee.firstName }}
                    </option>
                  </select>
                  <div class="invalid-feedback">{{ form.strategy.responsible.error }}</div>
                </div>
              </div>
              <div class="mb-3">
                <label for="strategyDescription" class="form-label">{{ $t("views.strategy.edit.description") }}</label>
                <RTEditor ref="strategyDescription" v-model="form.strategy.description.content" contentType="html" id="strategyDescription"></RTEditor>
                <div class="quill-invalid-feedback">{{ form.strategy.description.error }}</div>
              </div>
              <div v-if="!form.strategy.folder.content" class="row mt-3">
                <div class="mb-3">
                  <label for="strategyBenefit" class="form-label">{{ $t("views.strategy.edit.benefit") }}</label>
                  <RTEditor ref="strategyBenefit" v-model="form.strategy.benefit.content" contentType="html" id="strategyBenefit"></RTEditor>
                  <div class="quill-invalid-feedback">{{ form.strategy.benefit.error }}</div>
                </div>
                <div class="mb-3">
                  <label for="strategyRisk" class="form-label">{{ $t("views.strategy.edit.risk") }}</label>
                  <RTEditor ref="strategyRisk" v-model="form.strategy.risk.content" contentType="html" id="strategyRisk"></RTEditor>
                  <div class="quill-invalid-feedback">{{ form.strategy.risk.error }}</div>
                </div>
                <div class="row mb-3">
                  <div class="col-6">
                    <label for="strategyValidFrom" class="form-label">{{ $t("views.strategy.edit.validFrom") }}<span class="text-danger">*</span></label>
                    <input v-model="form.strategy.validFrom.content" type="date" id="strategyValidFrom" :class="form.strategy.validFrom.error === '' ? 'form-control' : 'form-control is-invalid'">
                    <div class="invalid-feedback">{{ form.strategy.validFrom.error }}</div>
                  </div>
                  <div class="col-6">
                    <label for="strategyValidTo" class="form-label">{{ $t("views.strategy.edit.validTo") }}<span class="text-danger">*</span></label>
                    <input v-model="form.strategy.validTo.content" type="date" id="strategyValidTo" :class="form.strategy.validTo.error === '' ? 'form-control' : 'form-control is-invalid'">
                    <div class="invalid-feedback">{{ form.strategy.validTo.error }}</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Review -->
            <div v-if="elementType === 'review'" class="form-link">
              <div class="mb-3">
                <label for="reviewDate" class="form-label">{{ $t("views.strategy.edit.reviewDate") }}<span class="text-danger">*</span></label>
                <input v-model="form.review.date.content" type="date" id="reviewDate" :class="form.review.date.error === '' ? 'form-control' : 'form-control is-invalid'">
                <div class="invalid-feedback">{{ form.review.date.error }}</div>
              </div>
              <div class="mb-3">
                <label for="reviewAssessment" class="form-label">{{ $t("views.strategy.edit.reviewAssessment") }}<span class="text-danger">*</span></label>
                <RTEditor ref="reviewAssessment" v-model="form.review.assessment.content" contentType="html" id="reviewAssessment"></RTEditor>
                <div class="quill-invalid-feedback">{{ form.review.assessment.error }}</div>
              </div>
              <div class="mb-3">
                <label for="reviewConversion" class="form-label">{{ $t("views.strategy.edit.reviewConversion") }}<span class="text-danger">*</span></label>
                <select v-model="form.review.conversion.content" id="reviewConversion" :class="form.review.conversion.error === '' ? 'form-select' : 'form-select is-invalid'">
                  <option value="0">0%</option>
                  <option value="1">25%</option>
                  <option value="2">50%</option>
                  <option value="3">75%</option>
                  <option value="4">100%</option>
                </select>
                <div class="invalid-feedback">{{ form.review.conversion.error }}</div>
              </div>
              <div class="mb-3">
                <label for="reviewPrediction" class="form-label">{{ $t("views.strategy.edit.reviewPrediction") }}<span class="text-danger">*</span></label>
                <select v-model="form.review.prediction.content" id="reviewPrediction" :class="form.review.prediction.error === '' ? 'form-select' : 'form-select is-invalid'">
                  <option value="Y">{{ $t("views.strategy.detail.review.prediction.y") }}</option>
                  <option value="D">{{ $t("views.strategy.detail.review.prediction.d") }}</option>
                  <option value="N">{{ $t("views.strategy.detail.review.prediction.n") }}</option>
                </select>
                <div class="invalid-feedback">{{ form.review.prediction.error }}</div>
              </div>
            </div>

            <!-- Measure -->
            <div v-if="elementType === 'measure'" class="form-link">
              <div class="mb-3">
                <label for="measureTitle" class="form-label">{{ $t("views.strategy.edit.measureTitle") }}<span class="text-danger">*</span></label>
                <input v-model="form.measure.title.content" id="measureTitle" :class="form.measure.title.error === '' ? 'form-control' : 'form-control is-invalid'">
                <div class="invalid-feedback">{{ form.measure.title.error }}</div>
              </div>
              <div class="mb-3">
                <label for="measureMeasure" class="form-label">{{ $t("views.strategy.edit.measureDescription") }}<span class="text-danger">*</span></label>
                <RTEditor ref="measureMeasure" v-model="form.measure.measure.content" contentType="html" id="measureMeasure"></RTEditor>
                <div class="quill-invalid-feedback">{{ form.measure.measure.error }}</div>
              </div>
              <div class="mb-3">
                <label for="measureResponsible" class="form-label">{{ $t("views.strategy.edit.measureResponsible") }}<span class="text-danger">*</span></label>
                <select v-model="form.measure.responsible.content" id="measureResponsible" :class="form.measure.responsible.error === '' ? 'form-select' : 'form-select is-invalid'">
                  <option v-for="employee in employees" :key="employee.id" :value="employee.id">
                    {{ employee.lastName }}, {{ employee.firstName }}
                  </option>
                </select>
                <div class="invalid-feedback">{{ form.measure.responsible.error }}</div>
              </div>
              <div class="mb-3">
                <label for="measureConversion" class="form-label">{{ $t("views.strategy.edit.measureConversion") }}</label>
                <select v-model="form.measure.conversion.content" id="measureConversion" :class="form.measure.conversion.error === '' ? 'form-select' : 'form-select is-invalid'">
                  <option value="0">0%</option>
                  <option value="1">25%</option>
                  <option value="2">50%</option>
                  <option value="3">75%</option>
                  <option value="4">100%</option>
                </select>
                <div class="invalid-feedback">{{ form.measure.conversion.error }}</div>
              </div>
              <div class="mb-3">
                <label for="measureValidFrom" class="form-label">{{ $t("views.strategy.edit.measureValidFrom") }}<span class="text-danger">*</span></label>
                <input v-model="form.measure.validFrom.content" type="date" id="measureValidFrom" :class="form.measure.validFrom.error === '' ? 'form-control' : 'form-control is-invalid'">
                <div class="invalid-feedback">{{ form.measure.validFrom.error }}</div>
              </div>
              <div class="mb-3">
                <label for="measureValidTo" class="form-label">{{ $t("views.strategy.edit.measureValidTo") }}<span class="text-danger">*</span></label>
                <input v-model="form.measure.validTo.content" type="date" id="measureValidTo" :class="form.measure.validTo.error === '' ? 'form-control' : 'form-control is-invalid'">
                <div class="invalid-feedback">{{ form.measure.validTo.error }}</div>
              </div>
            </div>

            <!-- Relationship -->
            <div v-if="elementType === 'relationship'" class="form-link">
              <div class="mb-3">
                <label for="relationshipType" class="form-label">{{ $t("views.strategy.edit.relationshipType") }}<span class="text-danger">*</span></label>
                <select v-model="form.relationship.type.content" id="relationshipType"  :class="form.relationship.type.error === '' ? 'form-control' : 'form-control is-invalid'">
                  <option value="objective">{{ $t("views.strategy.edit.relationshipTypeObjective") }}</option>
                  <option value="project">{{ $t("views.strategy.edit.relationshipTypeProject") }}</option>
                  <option value="kpi">{{ $t("views.strategy.edit.relationshipTypeKPI") }}</option>
                </select>
                <div class="invalid-feedback">{{ form.relationship.type.error }}</div>
              </div>
              <div class="mb-3">
                <div v-if="this.form.relationship.type.content === 'objective'" class="form-link">
                  <label for="relationshipObjective" class="form-label">{{ $t("views.strategy.edit.relationshipObjective") }}<span class="text-danger">*</span></label>
                  <select v-model="form.relationshipObjective.objectiveParentId.content" id="objectiveParentId" :class="form.relationshipObjective.objectiveParentId.error === '' ? 'form-control' : 'form-control is-invalid'">
                    <option v-for="objective in objectivesList" :key="objective.id" :value="objective.id" :disabled="objective.org ? true : false">
                      {{ objective.category === 1 ? objective.name.toUpperCase() : objective.name }}
                  </option>
                  </select>
                  <div class="invalid-feedback">{{ form.relationshipObjective.objectiveParentId.error }}</div>
                </div>
                <div v-if="this.form.relationship.type.content === 'project'" class="form-link">
                  <label for="relationshipProject" class="form-label">{{ $t("views.strategy.edit.relationshipProject") }}<span class="text-danger">*</span></label>
                  <select v-model="form.relationshipProject.projectParentId.content" id="projectParentId" :class="form.relationshipProject.projectParentId.error === '' ? 'form-control' : 'form-control is-invalid'">
                    <option v-for="project in projectsList" :key="project.id" :value="project.id" :disabled="project.folder ? true : false">
                      {{ project.name }}
                  </option>
                  </select>
                  <div class="invalid-feedback">{{ form.relationshipProject.projectParentId.error }}</div>
                </div>
                <div v-if="this.form.relationship.type.content === 'kpi'" class="form-link">
                  <label for="relationshipKPI" class="form-label">{{ $t("views.strategy.edit.relationshipKPI") }}<span class="text-danger">*</span></label>
                  <select v-model="form.relationshipKPI.kpiParentId.content" id="kpiParentId" :class="form.relationshipKPI.kpiParentId.error === '' ? 'form-control' : 'form-control is-invalid'">
                    <option v-for="kpi in kpisList" :key="kpi.id" :value="kpi.id" :disabled="kpi.folder ? true : false">
                      {{ kpi.name }}
                  </option>
                  </select>
                  <div class="invalid-feedback">{{ form.relationshipKPI.kpiParentId.error }}</div>
                </div>
              </div>
            </div>

            <!-- Trial -->
            <div v-if="elementType === 'trial'" class="form-link">
              <div class="mb-3">
                <label for="trialDescription" class="form-label">{{ $t("views.strategy.detail.trialDescription") }}<span class="text-danger">*</span></label>
                <input ref="trialDescription" v-model="form.trial.description.content" id="trialDescription" :class="form.trial.description.error === '' ? 'form-control' : 'form-control is-invalid'">
                <div class="invalid-feedback">{{ form.trial.description.error }}</div>
              </div>
            </div>
          </div>
          <div class="modal-footer bg-light justify-content-between">
            <div>
              <button v-if="elementType === 'strategy'" type="button" class="btn btn-sm btn-outline-danger" :class="selectedStrategy.hasNodes ? 'disabled' : ''" data-bs-dismiss="modal" data-bs-toggle="modal" data-bs-target="#strategyDeleteModal">{{ $t("general.delete") }}</button>
            </div>
            <div>
              <button type="button" class="btn btn-sm btn-outline-secondary me-2" data-bs-dismiss="modal">{{ $t("general.close") }}</button>
              <button @click="save()" type="button" class="btn btn-sm btn-primary">{{ $t("general.save") }}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { nextTick, ref } from 'vue'
import initializeForm from '../../composables/initializeForm'
import setFormContent from '../../composables/setFormContent'
import * as validation from '../../composables/validation'
import DocsInfoLink from '../../components/DocsInfoLink.vue'
import RTEditor from '../RTEditor.vue'
import { Modal } from 'bootstrap'

export default {
  name: 'StrategyEditModal',
  mounted () {
    const myModalDialog = this.$refs.strategyEditModal

    myModalDialog.addEventListener('shown.bs.modal', async () => {
      await nextTick() // Vue DOM fertig
      this.initializeForm() // jetzt sicher initialisieren
    })

    myModalDialog.addEventListener('hidden.bs.modal', event => {
      const mode = this.$parent.strategyModalMode
      if (mode !== 'add' && mode !== 'delete') {
        setFormContent(this.form, this.$store.state.selectedStrategy, 'strategy', this.elementType, this.$parent.strategyModalElementId)
      }
    })
  },

  data () {
    return {
      form: {
        strategy: {
          folder: {
            content: '',
            error: '',
            validate: []
          },
          orgUnit: {
            content: '',
            error: '',
            validate: []
          },
          parentId: {
            content: '',
            error: '',
            validate: []
          },
          Objective: {
            content: '',
            error: '',
            validate: []
          },
          name: {
            content: '',
            error: '',
            maxLength: 255,
            validate: ['empty', 'length']
          },
          description: {
            content: '',
            error: '',
            validate: [],
            rtEditor: true,
            ref: 'strategyDescription'
          },
          responsible: {
            content: '',
            error: '',
            validate: []
          },
          benefit: {
            content: '',
            error: '',
            validate: [],
            rtEditor: true,
            ref: 'strategyBenefit'
          },
          risk: {
            content: '',
            error: '',
            validate: [],
            rtEditor: true,
            ref: 'strategyRisk'
          },
          validFrom: {
            content: '',
            error: '',
            validate: ['empty', 'validFrom']
          },
          validTo: {
            content: '',
            error: '',
            validate: ['empty', 'validTo']
          }
        },
        review: {
          date: {
            content: '',
            error: '',
            validate: ['empty']
          },
          assessment: {
            content: '',
            error: '',
            validate: ['empty'],
            rtEditor: true,
            ref: 'reviewAssessment'
          },
          conversion: {
            content: '',
            error: '',
            validate: ['empty']
          },
          prediction: {
            content: '',
            error: '',
            validate: ['empty']
          }
        },
        measure: {
          title: {
            content: '',
            error: '',
            maxLength: 255,
            validate: ['empty', 'length']
          },
          measure: {
            content: '',
            error: '',
            validate: ['empty'],
            rtEditor: true,
            ref: 'measureMeasure'
          },
          responsible: {
            content: '',
            error: '',
            validate: ['empty']
          },
          conversion: {
            content: '',
            error: '',
            validate: []
          },
          validFrom: {
            content: '',
            error: '',
            validate: ['empty', 'validFrom']
          },
          validTo: {
            content: '',
            error: '',
            validate: ['empty', 'validTo']
          }
        },
        relationship: {
          type: {
            content: '',
            error: '',
            validate: ['empty']
          }
        },
        relationshipObjective: {
          objectiveParentId: {
            content: '',
            error: '',
            validate: ['empty']
          }
        },
        relationshipProject: {
          projectParentId: {
            content: '',
            error: '',
            validate: ['empty']
          }
        },
        relationshipKPI: {
          kpiParentId: {
            content: '',
            error: '',
            validate: ['empty']
          }
        },
        trial: {
          description: {
            content: '',
            error: '',
            maxLength: 1000,
            validate: ['empty', 'length']
          }
        }
      }
    }
  },
  components: {
    DocsInfoLink,
    RTEditor
  },
  computed: {
    selectedStrategy () {
      return this.$store.state.selectedStrategy
    },
    strategiesList () {
      return this.$store.state.strategiesList
    },
    orgUnitsList () {
      return this.$store.state.orgUnitsList
    },
    selectedOrgUnit () {
      return this.$store.state.selectedOrgUnit
    },
    objectivesList () {
      return this.$store.state.objectivesList
    },
    projectsList () {
      return this.$store.state.projectsList
    },
    kpisList () {
      return this.$store.state.kpisList
    },
    employees () {
      return this.$store.state.employees
    },
    elementId () {
      return this.$parent.strategyModalElementId
    },
    elementType () {
      return this.$parent.strategyModalElementType
    },
    mode () {
      return this.$parent.strategyModalMode
    },
    isAdmin () {
      return this.$store.state.user.role === 10387
    },
    modalTitle () {
      const titles = {
        strategy: {
          add: this.$t('views.strategy.edit.headingAddStrategy'),
          update: this.$t('views.strategy.edit.headingEditStrategy')
        },
        review: {
          add: this.$t('views.strategy.edit.headingAddReview'),
          update: this.$t('views.strategy.edit.headingEditReview')
        },
        measure: {
          add: this.$t('views.strategy.edit.headingAddMeasure'),
          update: this.$t('views.strategy.edit.headingEditMeasure')
        },
        relationship: {
          add: this.$t('views.strategy.edit.headingAddRelationship')
        },
        trial: {
          add: this.$t('views.strategy.edit.headingAddTrial'),
          update: this.$t('views.strategy.edit.headingEditTrial')
        }
      }
      return titles[this.elementType]?.[this.mode] ?? ''
    }
  },

  methods: {
    openModal (id, mode) {
      this.mode = mode
      if (mode === 'add') {
        // set default value for role
        this.form.relationship.objectiveParentId.content = ''
      }
    },
    async initializeForm () {
      const mode = this.$parent.strategyModalMode
      if (mode === 'add' || mode === 'addFromTrial') {
        initializeForm(this.form.strategy)
        initializeForm(this.form.review)
        initializeForm(this.form.measure)
        initializeForm(this.form.trial)
        if (this.$store.state.selectedStrategy.id) {
          this.form.strategy.orgUnit.content = this.$store.state.selectedStrategy.orgUnit
          this.form.strategy.parentId.content = this.$store.state.selectedStrategy.id
          if (this.$store.state.selectedStrategy.folder === false) {
            this.form.strategy.responsible.content = this.$store.state.selectedStrategy.responsible.id
            this.form.strategy.validFrom.content = this.$store.state.selectedStrategy.validFrom
            this.form.strategy.validTo.content = this.$store.state.selectedStrategy.validTo
          }
        } else {
          this.form.strategy.orgUnit.content = this.$store.state.orgUnitsList[0].id
        }
        const today = new Date().toISOString().substring(0, 10)
        this.form.review.date.content = today

        if (mode === 'addFromTrial') {
          this.form.measure.title.content = this.$parent.strategyModalElementName
          this.form.measure.validFrom.content = today
          this.$parent.strategyModalMode = 'add'
        }
      } else if (mode === 'update') {
        setFormContent(this.form, this.$store.state.selectedStrategy, 'strategy', this.elementType, this.$parent.strategyModalElementId)
      }
    },

    async save () {
      this.$store.commit('SET_LOADING_STATUS', true)

      if (this.form.strategy.folder.content) {
        this.form.strategy.validFrom.content = '2000-01-01'
        this.form.strategy.validTo.content = '2099-12-31'
      }

      let valid = 0
      if (this.elementType === 'strategy') valid = validation.fieldValidation(this.form.strategy)
      if (this.elementType === 'review') valid = validation.fieldValidation(this.form.review)
      if (this.elementType === 'measure') valid = validation.fieldValidation(this.form.measure)
      if (this.elementType === 'trial') valid = validation.fieldValidation(this.form.trial)
      if (this.elementType === 'relationship') {
        valid = validation.fieldValidation(this.form.relationship)
        if (this.form.relationship.type.content === 'objective') {
          valid = validation.fieldValidation(this.form.relationshipObjective)
        }
        if (this.form.relationship.type.content === 'project') {
          valid = validation.fieldValidation(this.form.relationshipProject)
        }
        if (this.form.relationship.type.content === 'kpi') {
          valid = validation.fieldValidation(this.form.relationshipKPI)
        }
      }
      // check existence of the new relationship
      if (valid === 0) {
        valid = validation.relationshipValidation(this.form, this.selectedStrategy)
      }

      if (valid === 0) {
        Object.keys(this.form[this.elementType]).forEach(field => {
          if (this.form[this.elementType][field].rtEditor) {
            const ref = this.form[this.elementType][field].ref
            if (this.$refs[ref]) {
              this.form[this.elementType][field].content = this.$refs[ref].getContent()
            }
          }
        })
        const elementData = Object.assign(
          ...Object.entries(this.form[this.elementType]).map(([key, value]) => ({ [key]: value.content }))
        )
        if (elementData.parentId === undefined) elementData.parentId = ''
        if (elementData.folder === '') elementData.folder = 'False'

        elementData.id = this.elementId
        let dispatchFunc = ''
        if (this.elementType !== 'strategy') {
          elementData.elementType = (this.elementType + 's')
          elementData.strategyId = this.selectedStrategy.id
          if (this.form.relationship.type.content === 'objective') elementData.objectiveParentId = this.form.relationshipObjective.objectiveParentId.content
          if (this.form.relationship.type.content === 'project') elementData.projectParentId = this.form.relationshipProject.projectParentId.content
          if (this.form.relationship.type.content === 'kpi') elementData.kpiParentId = this.form.relationshipKPI.kpiParentId.content
        }
        dispatchFunc = this.elementType === 'strategy' ? (this.mode + 'Strategy') : (this.mode + 'StrategyElement')
        await this.$store.dispatch(dispatchFunc, elementData).then(() => {
          this.closeModal()
          Object.keys(this.form[this.elementType]).forEach(field => {
            if (this.mode === 'add') {
              if (field === 'folder') {
                this.form[this.elementType][field].content = false
              } else {
                this.form[this.elementType][field].content = ''
              }
            }
            this.form[this.elementType][field].error = ''
          })
        })
      }

      this.$store.commit('SET_LOADING_STATUS', false)

      // Reload Data & Treeview
      if ((this.elementType === 'strategy') || this.elementType === 'review') {
        await this.$store.dispatch('getStrategies', { validFrom: this.$parent.filterValidFrom, validTo: this.$parent.filterValidTo })
      }
    },
    updateModalContent () {
      Object.keys(this.form[this.elementType]).forEach(field => {
        let selectedElement = this.selectedStrategy
        if (this.elementType !== 'strategy') {
          const elementTypePlural = this.elementType + 's'
          selectedElement = this.selectedStrategy[elementTypePlural].find(element => {
            return element.id === this.$parent.strategyModalElementId
          })
          this.$parent.selectedElement = selectedElement
        }
        if (field in selectedElement) {
          if (field === 'responsible') {
            this.form[this.elementType].responsible.content = selectedElement[field].id
          } else {
            this.form[this.elementType][field].content = selectedElement[field]
          }
          this.form[this.elementType][field].error = ''
        }
      })
    }
  },
  watch: {
    elementId () {
      this.updateModalContent()
    },
    mode (newMode) {
      if (newMode === 'add') {
        Object.keys(this.form[this.elementType]).forEach(field => {
          if (field === 'folder') {
            this.form[this.elementType][field].content = false
          } else {
            this.form[this.elementType][field].content = this.form[this.elementType][field].error = ''
          }
        })
        // set default values
        const today = new Date().toISOString().substring(0, 10)
        this.form.strategy.orgUnit.content = this.$store.state.orgUnitsList[0].id
        this.form.review.date.content = today
      } else if (newMode === 'update') {
        this.updateModalContent()
      }
    }
  },
  setup () {
    const strategyEditModal = ref(null)
    const closeModal = () => Modal.getInstance(strategyEditModal.value)?.hide()
    return { strategyEditModal, closeModal }
  }
}
</script>
